import {
    Button,
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridRow,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Dropdown,
    Field,
    Input,
    Option,
    TableCellLayout,
    type TableColumnDefinition,
    createTableColumn,
    makeStyles,
    tokens,
} from '@fluentui/react-components';
import { AddRegular, DeleteRegular } from '@fluentui/react-icons';
import { useCallback, useMemo, useState } from 'react';
import { z } from 'zod';

type CausingParty = {
    role: 'Mieter' | 'Eigentümer' | 'Sonstiger';
    name: string | null;
    phone: string | null;
    email: string | null;
    address: string | null;
    buildingUnit: string | null;
    liabilityInsuranceInsurer: string | null;
    liabilityInsuranceContractNumber: string | null;
};

type CausingPartiesTableProps = {
    parties: CausingParty[];
    onChange: (parties: CausingParty[]) => void;
    status: 'draft' | 'filing' | 'filed' | 'failed';
};

const useStyles = makeStyles({
    dialogBackdrop: {
        backgroundColor: 'transparent',
    },
});

export function CausingPartiesTable({ parties, onChange, status }: CausingPartiesTableProps) {
    const styles = useStyles();
    const [editingParty, setEditingParty] = useState<CausingParty | null>(null);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{ name?: string; email?: string }>({});

    const columns: TableColumnDefinition<CausingParty>[] = useMemo(
        () => [
            createTableColumn<CausingParty>({
                columnId: 'role',
                compare: (a, b) => a.role.localeCompare(b.role),
                renderHeaderCell: () => 'Rolle',
                renderCell: (item) => <TableCellLayout>{item.role}</TableCellLayout>,
            }),
            createTableColumn<CausingParty>({
                columnId: 'name',
                compare: (a, b) => (a.name ?? '').localeCompare(b.name ?? ''),
                renderHeaderCell: () => 'Name',
                renderCell: (item) => <TableCellLayout>{item.name}</TableCellLayout>,
            }),
            createTableColumn<CausingParty>({
                columnId: 'phone',
                compare: (a, b) => (a.phone ?? '').localeCompare(b.phone ?? ''),
                renderHeaderCell: () => 'Telefon',
                renderCell: (item) => <TableCellLayout>{item.phone}</TableCellLayout>,
            }),
            createTableColumn<CausingParty>({
                columnId: 'email',
                compare: (a, b) => (a.email ?? '').localeCompare(b.email ?? ''),
                renderHeaderCell: () => 'E-Mail',
                renderCell: (item) => <TableCellLayout>{item.email}</TableCellLayout>,
            }),
            createTableColumn<CausingParty>({
                columnId: 'address',
                compare: (a, b) => (a.address ?? '').localeCompare(b.address ?? ''),
                renderHeaderCell: () => 'Adresse',
                renderCell: (item) => <TableCellLayout>{item.address}</TableCellLayout>,
            }),
            createTableColumn<CausingParty>({
                columnId: 'buildingUnit',
                compare: (a, b) => (a.buildingUnit ?? '').localeCompare(b.buildingUnit ?? ''),
                renderHeaderCell: () => 'Wohnung',
                renderCell: (item) => <TableCellLayout>{item.buildingUnit}</TableCellLayout>,
            }),
            createTableColumn<CausingParty>({
                columnId: 'liabilityInsurance',
                compare: (a, b) => (a.liabilityInsuranceInsurer ?? '').localeCompare(b.liabilityInsuranceInsurer ?? ''),
                renderHeaderCell: () => 'Haftpflicht',
                renderCell: (item) => <TableCellLayout>{item.liabilityInsuranceInsurer}</TableCellLayout>,
            }),
            createTableColumn<CausingParty>({
                columnId: 'liabilityInsuranceContract',
                compare: (a, b) =>
                    (a.liabilityInsuranceContractNumber ?? '').localeCompare(b.liabilityInsuranceContractNumber ?? ''),
                renderHeaderCell: () => 'Vertrag',
                renderCell: (item) => <TableCellLayout>{item.liabilityInsuranceContractNumber}</TableCellLayout>,
            }),
        ],
        []
    );

    const handleAddParty = useCallback(() => {
        setEditingParty({
            role: 'Mieter',
            name: null,
            phone: null,
            email: null,
            address: null,
            buildingUnit: null,
            liabilityInsuranceInsurer: null,
            liabilityInsuranceContractNumber: null,
        });
        setEditingIndex(null);
        setValidationErrors({});
        setIsDialogOpen(true);
    }, []);

    const handleEditParty = useCallback((party: CausingParty, index: number) => {
        setEditingParty({ ...party });
        setEditingIndex(index);
        setValidationErrors({});
        setIsDialogOpen(true);
    }, []);

    const handleSaveParty = useCallback(() => {
        if (!editingParty) {
            return;
        }

        // Validate
        const errors: { name?: string; email?: string } = {};

        if (!editingParty.name || editingParty.name.trim() === '') {
            errors.name = 'Name ist erforderlich';
        }

        if (editingParty.email) {
            const emailValidation = z.string().email().safeParse(editingParty.email);
            if (!emailValidation.success) {
                errors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
            }
        }

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        // Save if valid
        if (editingIndex !== null) {
            const updated = [...parties];
            updated[editingIndex] = editingParty;
            onChange(updated);
        } else {
            onChange([...parties, editingParty]);
        }

        setIsDialogOpen(false);
        setEditingParty(null);
        setEditingIndex(null);
        setValidationErrors({});
    }, [editingParty, editingIndex, parties, onChange]);

    const handleDeleteParty = useCallback(() => {
        if (editingIndex === null) {
            return;
        }

        onChange(parties.filter((_, i) => i !== editingIndex));
        setIsDialogOpen(false);
        setEditingParty(null);
        setEditingIndex(null);
    }, [editingIndex, parties, onChange]);

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: 0 }}>
                {parties.length !== 0 ? (
                    <div
                        style={{
                            border: `1px solid ${tokens.colorNeutralStroke1}`,
                            borderRadius: tokens.borderRadiusMedium,
                            overflow: 'auto',
                            minWidth: 0,
                        }}
                    >
                        <DataGrid
                            items={parties}
                            columns={columns}
                            sortable
                            getRowId={(item) => String(parties.indexOf(item))}
                        >
                            <DataGridHeader>
                                <DataGridRow style={{ minHeight: '44px' }}>
                                    {({ renderHeaderCell }) => (
                                        <DataGridHeaderCell style={{ fontWeight: tokens.fontWeightSemibold }}>
                                            {renderHeaderCell()}
                                        </DataGridHeaderCell>
                                    )}
                                </DataGridRow>
                            </DataGridHeader>
                            <DataGridBody<CausingParty>>
                                {({ item, rowId }) => (
                                    <DataGridRow<CausingParty>
                                        key={rowId}
                                        onClick={() => handleEditParty(item, Number(rowId))}
                                        style={{
                                            cursor: 'pointer',
                                            borderBottom: Number(rowId) === parties.length - 1 ? 'none' : undefined,
                                        }}
                                    >
                                        {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                                    </DataGridRow>
                                )}
                            </DataGridBody>
                        </DataGrid>
                    </div>
                ) : null}
                <Button
                    appearance={parties.length === 0 ? 'secondary' : 'subtle'}
                    icon={<AddRegular />}
                    onClick={handleAddParty}
                    disabled={status !== 'draft'}
                >
                    Neue Person
                </Button>
            </div>

            <Dialog open={isDialogOpen} onOpenChange={(_, data) => setIsDialogOpen(data.open)}>
                <DialogSurface backdrop={{ className: styles.dialogBackdrop }}>
                    <DialogBody>
                        <DialogTitle>{editingIndex !== null ? 'Person bearbeiten' : 'Neue Person'}</DialogTitle>
                        <DialogContent>
                            {editingParty && (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: tokens.spacingVerticalL,
                                        minWidth: 0,
                                    }}
                                >
                                    <Field label="Rolle" required>
                                        <Dropdown
                                            value={editingParty.role}
                                            selectedOptions={[editingParty.role]}
                                            onOptionSelect={(_, data) =>
                                                setEditingParty({
                                                    ...editingParty,
                                                    role: data.optionValue as 'Mieter' | 'Eigentümer' | 'Sonstiger',
                                                })
                                            }
                                            style={{ minWidth: 0 }}
                                        >
                                            <Option value="Mieter">Mieter</Option>
                                            <Option value="Eigentümer">Eigentümer</Option>
                                            <Option value="Sonstiger">Sonstiger</Option>
                                        </Dropdown>
                                    </Field>
                                    <Field
                                        label="Name"
                                        required
                                        validationState={validationErrors.name ? 'error' : undefined}
                                        validationMessage={validationErrors.name}
                                    >
                                        <Input
                                            value={editingParty.name ?? ''}
                                            onChange={(_, data) =>
                                                setEditingParty({
                                                    ...editingParty,
                                                    name: data.value || null,
                                                })
                                            }
                                            style={{ minWidth: 0 }}
                                        />
                                    </Field>
                                    <Field label="Telefon">
                                        <Input
                                            value={editingParty.phone ?? ''}
                                            onChange={(_, data) =>
                                                setEditingParty({
                                                    ...editingParty,
                                                    phone: data.value || null,
                                                })
                                            }
                                            style={{ minWidth: 0 }}
                                        />
                                    </Field>
                                    <Field
                                        label="E-Mail"
                                        validationState={validationErrors.email ? 'error' : undefined}
                                        validationMessage={validationErrors.email}
                                    >
                                        <Input
                                            type="email"
                                            value={editingParty.email ?? ''}
                                            onChange={(_, data) =>
                                                setEditingParty({
                                                    ...editingParty,
                                                    email: data.value || null,
                                                })
                                            }
                                            style={{ minWidth: 0 }}
                                        />
                                    </Field>
                                    <Field label="Adresse">
                                        <Input
                                            value={editingParty.address ?? ''}
                                            onChange={(_, data) =>
                                                setEditingParty({
                                                    ...editingParty,
                                                    address: data.value || null,
                                                })
                                            }
                                            style={{ minWidth: 0 }}
                                        />
                                    </Field>
                                    <Field label="Wohnung">
                                        <Input
                                            value={editingParty.buildingUnit ?? ''}
                                            onChange={(_, data) =>
                                                setEditingParty({
                                                    ...editingParty,
                                                    buildingUnit: data.value || null,
                                                })
                                            }
                                            style={{ minWidth: 0 }}
                                        />
                                    </Field>
                                    <Field label="Name des Versicherers der Haftpflichtversicherung">
                                        <Input
                                            value={editingParty.liabilityInsuranceInsurer ?? ''}
                                            onChange={(_, data) =>
                                                setEditingParty({
                                                    ...editingParty,
                                                    liabilityInsuranceInsurer: data.value || null,
                                                })
                                            }
                                            style={{ minWidth: 0 }}
                                        />
                                    </Field>
                                    <Field label="Vertragsnummer zur Haftpflichtversicherung">
                                        <Input
                                            value={editingParty.liabilityInsuranceContractNumber ?? ''}
                                            onChange={(_, data) =>
                                                setEditingParty({
                                                    ...editingParty,
                                                    liabilityInsuranceContractNumber: data.value || null,
                                                })
                                            }
                                            style={{ minWidth: 0 }}
                                        />
                                    </Field>
                                </div>
                            )}
                        </DialogContent>
                        <DialogActions>
                            {editingIndex !== null && (
                                <Button appearance="secondary" icon={<DeleteRegular />} onClick={handleDeleteParty}>
                                    Löschen
                                </Button>
                            )}
                            <DialogTrigger disableButtonEnhancement>
                                <Button appearance="secondary">Abbrechen</Button>
                            </DialogTrigger>
                            <Button appearance="primary" onClick={handleSaveParty}>
                                Übernehmen
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </>
    );
}
